import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Gamepad2, Lock, User, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        username,
        password
      });

      if (response.data.success) {
        // Save token and user data to localStorage
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        // Redirect based on role
        const user = response.data.user;
        if (user.role === 'admin') {
          navigate('/admin');
        } else if (user.role === 'supervisor') {
          navigate('/admin');  // Supervisor also goes to admin dashboard
        } else if (user.role === 'kasir') {
          navigate('/kasir');
        }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Username atau password salah');
      } else {
        setError('Terjadi kesalahan. Silakan coba lagi.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden billing-login-shell">
      {/* Animated Gaming Background */}
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        {/* Base Animated Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 animate-gradient-shift"></div>
        
        {/* Gaming Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(59, 130, 246, 0.1) 2px, rgba(59, 130, 246, 0.1) 4px),
              repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(168, 85, 247, 0.1) 2px, rgba(168, 85, 247, 0.1) 4px)
            `,
            animation: 'gridMove 20s linear infinite'
          }}></div>
        </div>
      </div>
      
      {/* Animated Gaming Particles & Shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large floating orbs with pulse */}
        <div className="absolute top-20 left-10 w-40 h-40 bg-blue-500/20 rounded-full blur-3xl animate-float" style={{animationDuration: '6s'}}></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl animate-float" style={{animationDuration: '8s', animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 right-1/4 w-36 h-36 bg-pink-500/20 rounded-full blur-3xl animate-float" style={{animationDuration: '7s', animationDelay: '0.5s'}}></div>
        <div className="absolute top-1/3 left-1/3 w-32 h-32 bg-cyan-500/20 rounded-full blur-3xl animate-float" style={{animationDuration: '9s', animationDelay: '2s'}}></div>
        
        {/* Gaming Icons - Gamepad shapes */}
        <div className="absolute top-10 right-20 opacity-5">
          <Gamepad2 className="w-24 h-24 text-blue-400 animate-spin-slow" />
        </div>
        <div className="absolute bottom-10 left-20 opacity-5">
          <Gamepad2 className="w-32 h-32 text-purple-400 animate-spin-slow-reverse" />
        </div>
        
        {/* Small pixel particles */}
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-blue-400 animate-ping" style={{animationDelay: '0s'}}></div>
        <div className="absolute top-1/3 right-1/3 w-2 h-2 bg-purple-400 animate-ping" style={{animationDelay: '0.5s'}}></div>
        <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-pink-400 animate-ping" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-1/3 right-1/4 w-2 h-2 bg-cyan-400 animate-ping" style={{animationDelay: '1.5s'}}></div>
        
        {/* Hexagon shapes */}
        <div className="absolute top-1/4 right-1/4 w-16 h-16 border-2 border-blue-500/20 rotate-45 animate-spin-very-slow"></div>
        <div className="absolute bottom-1/4 left-1/4 w-20 h-20 border-2 border-purple-500/20 rotate-12 animate-spin-very-slow-reverse"></div>
      </div>
      {/* Login Card - Glassmorphism Effect */}
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-10">
          {/* Logo/Title with Gaming Icon */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <img src="/logo-rimuru.png" alt="Rimuru Game Suite" className="h-24 w-auto mx-auto drop-shadow-2xl" />
                <div className="absolute -top-2 -right-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full p-2 animate-bounce">
                  <Gamepad2 className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white drop-shadow-lg mb-2">Rimuru Game Suite</h1>
            <p className="text-blue-200"> Rental Management System</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            {error && (
              <div className="bg-red-500/20 backdrop-blur-sm border border-red-400/50 text-red-100 px-4 py-3 rounded-xl text-sm animate-shake">
                 {error}
              </div>
            )}

            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-white mb-2">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-blue-300" />
                </div>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/10 backdrop-blur-md border border-white/30 rounded-xl text-white placeholder-blue-200 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition"
                  placeholder="Masukkan username"
                  required
                  autoFocus
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-blue-300" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 bg-white/10 backdrop-blur-md border border-white/30 rounded-xl text-white placeholder-blue-200 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition"
                  placeholder="Masukkan password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-blue-300 hover:text-white transition"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-4 px-4 rounded-xl transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-2xl transform hover:scale-105 disabled:transform-none flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Memproses...
                </>
              ) : (
                <>
                  <Gamepad2 className="h-5 w-5" />
                  Masuk ke Dashboard
                </>
              )}
            </button>
          </form>
        </div>

        {/* Decorative Elements */}
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-3/4 h-4 bg-gradient-to-r from-transparent via-blue-500/50 to-transparent blur-xl"></div>
      </div>
    </div>
  );
}

export default Login;
